import { FC, ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';

import { EditIcon, TrashBinIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { PopoverClose } from '../../../components/popover/index';
import { Tooltip } from '../../../components/tooltip/index';

type VaultFileManageActionsProps = {
  isDisabled: boolean;
  onDelete: () => void;
  onRename: () => void;
};

const wrapWithTooltip = (isDisabled: boolean, node: ReactElement) => {
  if (!isDisabled) return node;

  return (
    <Tooltip content={<AppFormattedMessage id="DOCUSIGN_VAULT_FILE_ACTION_DISABLED" />}>
      <div className="w-full">{node}</div>
    </Tooltip>
  );
};

export const VaultFileManageActions: FC<VaultFileManageActionsProps> = ({
  isDisabled,
  onRename,
  onDelete,
}) => {
  const itemClassName = (isDestructive = false) =>
    twMerge(
      'flex w-full items-center gap-2 p-[6px] pr-8 text-sm',
      isDisabled
        ? 'cursor-not-allowed bg-white text-gray-300 hover:bg-white'
        : twMerge('text-gray-700 hover:bg-gray-50', isDestructive && 'text-fireside-500'),
    );

  const renameAction = (
    <PopoverClose
      className={itemClassName()}
      data-permission-required
      disabled={isDisabled}
      onClick={onRename}
    >
      <EditIcon iconColor={isDisabled ? '#D0D5DD' : undefined} />
      <span
        className={twMerge('text-sm font-normal', isDisabled ? 'text-gray-300' : 'text-gray-700')}
      >
        <AppFormattedMessage id="RENAME" />
      </span>
    </PopoverClose>
  );

  const deleteAction = (
    <PopoverClose
      className={itemClassName(true)}
      data-permission-required
      disabled={isDisabled}
      onClick={onDelete}
    >
      <TrashBinIcon className="size-4" iconColor={isDisabled ? '#D0D5DD' : '#F04438'} />
      <span
        className={twMerge(
          'text-sm font-normal',
          isDisabled ? 'text-gray-300' : 'text-fireside-500',
        )}
      >
        <AppFormattedMessage id="DELETE" />
      </span>
    </PopoverClose>
  );

  return (
    <>
      {wrapWithTooltip(isDisabled, renameAction)}
      {wrapWithTooltip(isDisabled, deleteAction)}
    </>
  );
};
